// @flow
import { b2cPagesService, url as urlUtils } from '@clubmed/utils';

import { PAGE_TRACKING } from 'Client/cms/toolkit/constants';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { buildProductArgs } from 'Utils/graphql';
import { LINE_COMMON_TRACKING } from 'Utils/request/requestDictionary';
import {
  getDefaultTrackingProps,
  getDestinationTrackingProps,
  getDestinationTravelTrackingProps,
  getLandingTrackingProps,
  getOfferTrackingProps,
  getPrebookingTrackingProps,
  getResortTrackingProps,
  getHomepageTrackingProps,
} from './pageTracking.utils';

type BookingCriteria = {
  initialDuration: number,
  initialNumberOfAdults: number,
  initialNumberOfChildren: number,
};

type GQLProps = {
  resort: Resort,
  price: {
    bestPrice: {
      value: number,
    },
    initialPrice: {
      value: number,
    },
    termsAndConditions: {
      departureCity: {
        name: string,
        value: string,
      },
      departureDate: number,
    },
  },
  bookingCriteria: BookingCriteria,
};

const { PAGE_TYPES } = b2cPagesService;
const {
  DESTINATION,
  DESTINATION_TOUR,
  DESTINATION_MICE,
  DESTINATION_TRAVEL,
  RESORT,
  TOUR,
  CRUISE,
  LANDING,
  LANDING_MICE,
  OFFER,
  PREBOOKING,
  RESORT_MICE,
  HOMEPAGE,
  HOME,
} = PAGE_TYPES;

export const pageTrackingResolver = {
  name: PAGE_TRACKING,
  props(appContext: AppContext, pageContext: PageContext, location: ProductRouterLocation) {
    const { pathname: pagePath } = location;
    const pageProps = { ...pageContext, pagePath };
    return {
      trackingData(props: GQLProps) {
        const { resort, price, bookingCriteria } = props;
        const pageData = { resort, price, bookingCriteria };
        switch (pageContext.pageType) {
          case OFFER:
            return getOfferTrackingProps(pageProps);
          case DESTINATION:
          case DESTINATION_TOUR:
          case DESTINATION_MICE:
            return getDestinationTrackingProps(pageProps);
          case DESTINATION_TRAVEL:
            return getDestinationTravelTrackingProps(pageProps);
          case RESORT:
          case TOUR:
          case CRUISE:
          case RESORT_MICE:
            return getResortTrackingProps({ ...pageProps, pageData });
          case LANDING:
          case LANDING_MICE:
            return getLandingTrackingProps(pageProps, resort);
          case PREBOOKING:
            return getPrebookingTrackingProps(pageProps);
          case HOME:
          case HOMEPAGE:
            return getHomepageTrackingProps(pageProps);
          default:
            return getDefaultTrackingProps(pageProps);
        }
      },
    };
  },
  resolve(
    appContext: AppContext,
    props: mixed,
    pageContext: PageContext,
    location: ProductRouterLocation,
  ): Promise<GQLProps> {
    const { pageType, pageMeta: { resortId } = {} } = pageContext;
    const { urlParams, queryParams } = location;
    // Fetch data for resort, resort_mice, tour and cruise pages only
    if (![RESORT, TOUR, CRUISE, RESORT_MICE, LANDING].includes(pageType) || !resortId) {
      return Promise.resolve({});
    }
    const productArgs = buildProductArgs(resortId, urlParams);
    let query;
    if (pageType === LANDING) {
      query = `query {
        resort(${productArgs}) {
          id
          type
        }
      }`;
    } else {
      const offerName = queryParams.offer ? urlUtils.decodePath(queryParams.offer) : '';
      const productArgsWithOffer = buildProductArgs(resortId, urlParams, { offerName });
      query = `query {
        resort(${productArgs}) {
          id type name comfort seasonName image destination {country area} adultOnly
        }
        price(${productArgsWithOffer}) {
          termsAndConditions {
            departureDate departureCity {value name}
          }
          bestPrice {value}
          initialPrice {value}
        }
        bookingCriteria(${productArgs}) {
          initialDuration(${productArgsWithOffer})
          initialNumberOfAdults(${productArgsWithOffer})
          initialNumberOfChildren
        }
      }`;
    }

    return graphQLClient.runQuery(query, appContext, {
      requestName: LINE_COMMON_TRACKING,
      strictErrors: false,
    });
  },
};
